// Portal Settings → Workflow & Disbursement → the on/off switch for the Admin release of a
// disbursement lock. It is off until an Admin turns it on here, and only the release button
// on the disbursement screen is behind it: the lock itself always stands.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LockKeyhole } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import {
  getDisbursementLockReleaseSwitch,
  setDisbursementLockReleaseSwitch,
} from "@/lib/api";
import { formatUkUtc } from "@/lib/disbursementLock";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

/** One line on who last changed the switch and when, or null when it never was. */
export function lastChangeLine(switchState) {
  if (!switchState?.changed_at && !switchState?.changed_by) return null;
  const by = switchState.changed_by || "someone";
  const reason = switchState.reason ? `: ${switchState.reason}` : "";
  return `Last changed by ${by} on ${formatUkUtc(switchState.changed_at)}${reason}`;
}

export default function DisbursementLockReleaseCard() {
  const { user, apiReady } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [state, setState] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetched only for an Admin (the endpoint refuses anyone else) and only once the token
  // getter is registered. Returns a cancel function for the effect.
  const load = useCallback(() => {
    if (!apiReady || !isAdmin) return undefined;
    let cancelled = false;
    setLoadFailed(false);
    getDisbursementLockReleaseSwitch()
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch(() => {
        if (cancelled) return;
        // No stale state beside the error: a switch that cannot be read offers no control.
        setState(null);
        setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [apiReady, isAdmin]);

  useEffect(() => load(), [load]);

  const turnOn = state?.enabled !== true;

  const apply = async () => {
    const stated = reason.trim();
    if (!stated) return;
    try {
      setSaving(true);
      const next = await setDisbursementLockReleaseSwitch({ enabled: turnOn, reason: stated });
      setState(next);
      setLoadFailed(false);
      setReason("");
      toast.success(
        next.enabled
          ? "Releasing a disbursement lock is switched on."
          : "Releasing a disbursement lock is switched off."
      );
      if (next.audit_logged === false) {
        toast.warning("The change is stored, but its audit log entry could not be written. Tell an engineer.", { duration: 15000 });
      }
    } catch (error) {
      toast.error(errorDetailMessage(error, "Could not change the switch"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="disbursement-lock-release-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LockKeyhole className="w-5 h-5 text-amber-600" />
          Disbursement lock release
        </CardTitle>
        <CardDescription>
          Lets an Admin release a locked, unfunded disbursement from the deal screen after the
          payment has been checked. It is off until switched on here. Switching it off removes the
          Release lock button and refuses the release; the lock on a deal always stands, and a
          second send stays refused.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!isAdmin && (
          <p className="text-sm text-muted-foreground" data-testid="release-switch-admin-only">
            Only an Admin can see or change this switch.
          </p>
        )}
        {isAdmin && loadFailed && (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-red-700" data-testid="release-switch-load-failed">
              The switch could not be read. Releasing is treated as off until it can be.
            </p>
            <Button variant="outline" size="sm" onClick={load} data-testid="release-switch-retry-btn">
              Retry
            </Button>
          </div>
        )}
        {isAdmin && !loadFailed && !state && (
          <p className="text-sm text-muted-foreground" data-testid="release-switch-loading">
            <Loader2 className="w-4 h-4 mr-1 inline animate-spin" />
            Loading
          </p>
        )}
        {isAdmin && state && (
          <>
            <p className="text-sm" data-testid="release-switch-state">
              Releasing a lock is currently{" "}
              <strong>{state.enabled ? "ON" : "OFF"}</strong>
              {state.state === "missing" ? " (never switched on)" : ""}
              {state.state === "malformed"
                ? " (the switch record is damaged; switching it on again repairs it)"
                : ""}
              {state.state === "unreadable" ? " (the switch could not be read)" : ""}.
            </p>
            {lastChangeLine(state) && (
              <p className="text-xs text-muted-foreground" data-testid="release-switch-last-change">
                {lastChangeLine(state)}
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="release-switch-reason">Reason (required)</Label>
              <Textarea
                id="release-switch-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                rows={2}
                placeholder={turnOn ? "Why is it being switched on?" : "Why is it being switched off?"}
                data-testid="release-switch-reason"
              />
            </div>
            <Button
              variant={turnOn ? "default" : "destructive"}
              onClick={apply}
              disabled={!reason.trim() || saving}
              data-testid="release-switch-apply-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {turnOn ? "Switch on" : "Switch off"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
